/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Text,
    Flex,
    IconButton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '../../hooks/use-toast'
import ConfirmationModal from '../confirmation-modal/index'
import {CloseIcon} from '../custom-icons'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation, usePromotions} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

export const REMOVE_COUPON_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'CONFIRM REMOVE PROMO CODE',
        id: 'confirmation_modal.remove_coupon.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this promo code?',
        id: 'confirmation_modal.remove_coupon.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove promo',
        id: 'confirmation_modal.remove_coupon.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove promo',
        id: 'confirmation_modal.remove_coupon.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep promo',
        id: 'confirmation_modal.remove_coupon.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep promo',
        id: 'confirmation_modal.remove_coupon.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

/**
 * CouponApplied is a React functional component that displays information about an applied coupon
 * within a shopping basket. It provides functionality to remove the coupon and displays relevant
 * messages based on the coupon's status.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.item - The coupon item object containing details about the applied coupon.
 * @param {string} props.item.code - The code of the applied coupon.
 * @param {string} props.item.statusCode - The status code of the coupon, indicating its applicability.
 * @param {string} props.item.couponItemId - The unique identifier for the coupon item in the basket.
 *
 * @returns {JSX.Element} A JSX element that renders the coupon information, including the coupon code,
 * its status, and a description. It also includes a button to remove the coupon if applicable.
 *
 * @example
 * <CouponApplied item={{ code: 'SAVE20', statusCode: 'applied', couponItemId: '12345' }} />
 *
 * @remarks
 * This component utilizes several hooks and context providers:
 * - `useIntl` for internationalization and formatting messages.
 * - `useCurrentBasket` to access the current shopping basket data.
 * - `useToast` to display success or error messages.
 * - `useShopperBasketsMutation` to handle the mutation for removing a coupon.
 * - `usePromotions` to fetch promotion details based on the coupon's promotion ID.
 *
 * The component also includes a confirmation modal to confirm the removal of a coupon.
 */
export const CouponApplied = ({item}) => {
    const intl = useIntl()
    const [description, setDescription] = useState()

    const modalProps = useDisclosure()

    const showRemoveCouponConfirmation = () => {
        modalProps.onOpen()
    }

    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const toast = useToast()
    const removeCouponAppliedMutation = useShopperBasketsMutation('removeCouponFromBasket')

    /**
     * Asynchronously removes an applied coupon from the user's basket.
     *
     * This function utilizes a mutation to remove a coupon identified by the provided
     * `couponItemId` from the current basket. Upon successful removal, a success toast
     * notification is displayed to inform the user that the promotion has been removed.
     * If an error occurs during the removal process, an error toast notification is shown
     * to alert the user of the failure.
     *
     * @async
     * @function removeCouponApplied
     * @param {string} couponItemId - The unique identifier of the coupon to be removed.
     * @returns {void} This function does not return a value.
     *
     * @example
     * removeCouponApplied('12345');
     *
     * @see {@link removeCouponAppliedMutation} for the mutation used to remove the coupon.
     * @see {@link toast} for displaying notifications to the user.
     */
    const removeCouponApplied = async (couponItemId) => {
        if (!couponItemId) {
            return
        }
        removeCouponAppliedMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: () => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_CouponApplied.info.promo_removed'
                        }),
                        description: '',
                        status: 'success'
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        description: '',
                        status: 'error'
                    })
                }
            }
        )
    }

    /**
     * Returns the promotion id for the given coupon code
     * Promotion ids can be collected from order, product and shipping level price adjustments
     *
     * @param {string} couponCode
     * @returns {string}
     */
    function getPromotionIdForCouponCode(couponCode) {
        const findPromotionId = (adjustments) => {
            for (const adjustment of adjustments) {
                if (adjustment.couponCode === couponCode) {
                    return adjustment.promotionId
                }
            }
            return null
        }

        // Check order-level price adjustments
        if (basket.orderPriceAdjustments) {
            const promotionId = findPromotionId(basket.orderPriceAdjustments)
            if (promotionId) return promotionId
        }

        // Check product-level price adjustments
        if (Array.isArray(basket.productItems)) {
            for (const product of basket.productItems) {
                if (product.priceAdjustments) {
                    const promotionId = findPromotionId(product.priceAdjustments)
                    if (promotionId) return promotionId
                }
            }
        }

        // Check shipping-level price adjustments
        if (Array.isArray(basket.shippingItems)) {
            for (const shipping of basket.shippingItems) {
                if (shipping.priceAdjustments) {
                    const promotionId = findPromotionId(shipping.priceAdjustments)
                    if (promotionId) return promotionId
                }
            }
        }

        // If no promotionId is found
        return null
    }

    const promoId = useMemo(() => {
        return getPromotionIdForCouponCode(item.code)
    }, [item.code])

    const {data: res} = usePromotions(
        {
            parameters: {
                ids: promoId
            }
        },
        {
            enabled: true
        }
    )
    const promos = res?.data || []

    useEffect(() => {
        setDescription(promos[0]?.calloutMsg)
    }, [promos])

    return (
        <Box p={16} pr={130} bg="lightBeige" mt="16px" position={'relative'}>
            <Flex alignItems="center">
                <Text flex="1" variant={'descriptor'}>
                    {item.code}
                </Text>
                <Flex position={'absolute'} alignItems="center" right={0} top={0}>
                    <Text
                        as="span"
                        variant="descriptor"
                        color={item.statusCode === 'no_applicable_promotion' ? 'orange' : 'green'}
                        textTransform={'uppercase'}
                        maxWidth={100}
                        display={'inline-block'}
                        textAlign={'right'}
                    >
                        {item.statusCode === 'no_applicable_promotion' ? (
                            <FormattedMessage
                                defaultMessage="No applicable promotion"
                                id={'CouponApplied.badge.no_applicable_promotion'}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Applied"
                                id={'CouponApplied.accordion.applied'}
                            />
                        )}
                    </Text>
                    {!basket.orderNo && (
                        <IconButton
                            icon={<CloseIcon boxSize="12px" />}
                            aria-label={intl.formatMessage({
                                defaultMessage: 'Remove promo code',
                                id: 'CouponApplied.accordion.button.remove'
                            })}
                            h={'44px'}
                            onClick={showRemoveCouponConfirmation}
                        />
                    )}
                </Flex>
            </Flex>
            {description && (
                <Text mt="8" variant={'bodySmall'} color="darkGray">
                    {description}
                </Text>
            )}
            <ConfirmationModal
                {...REMOVE_COUPON_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={() => removeCouponApplied(item.couponItemId)}
                {...modalProps}
            />
        </Box>
    )
}

CouponApplied.propTypes = {
    /** Props applied to inner AccordionItem. Useful for style overrides. */
    item: PropTypes.object
}

export default CouponApplied
